// Test-taking strategy and study-planning guide — shared across both
// sections, rather than duplicated inside the Math and Reading and Writing
// guides. Paragraphs are plain strings, callout text objects
// { text, label, kind }, or structured blocks { type: 'steps' | 'list', ... }
// — steps/list blocks render as a numbered or bulleted list instead of one
// run-on sentence. kind is 'example', 'tip', or 'mistake' and controls the
// callout badge color in StudyGuide.css.

export const documentTitle = 'Test-Taking Strategies';
export const documentSlug = 'test-strategies';

export const parts = [
  {
    id: 'part-test-taking',
    title: 'When You\'re Not Sure of the Answer',
    sections: [
      {
        id: 'strategy-no-penalty',
        heading: "There's No Penalty for Guessing",
        paragraphs: [
          'Only correct answers add to your score — a wrong answer and a blank question are scored exactly the same way (zero points). That means leaving a question blank is never better than guessing, and it\'s often worse: a blind guess among four choices has a 25% chance of being right, and eliminating even one wrong choice first improves those odds further. Always select something before time runs out, even on a question you\'re completely stuck on.',
        ],
      },
      {
        id: 'strategy-elimination',
        heading: 'Process of Elimination',
        paragraphs: [
          'Cross out any answer choice you can disprove for a specific, concrete reason — not just a vague feeling that it seems wrong. On math, this might mean a choice has the wrong units or doesn\'t satisfy a constraint stated in the problem; on reading and writing, it might mean a choice is grammatically wrong, off-topic, or answers a different question than the one being asked. Narrowing four choices down to two dramatically improves your odds even if you end up guessing between the last two.',
        ],
      },
      {
        id: 'strategy-backsolve',
        heading: 'Backsolving: Plug In the Answer Choices',
        paragraphs: [
          'If a math question describes a situation and asks for a numeric value, and you\'re not sure how to set up the algebra, try plugging each answer choice back into the problem and see which one makes everything true. Start with an answer choice in the middle of the range (like B or C on a four-choice question) — if it makes the result too large or too small, that tells you which direction to try next, and often lets you skip testing the remaining choices entirely.',
        ],
      },
      {
        id: 'strategy-plug-in-numbers',
        heading: 'Plug In Your Own Numbers',
        paragraphs: [
          'For math questions that ask which expression is "equivalent to" another, or how changing one variable affects another, pick simple numbers that satisfy any constraints given in the problem, compute the actual answer using those numbers, and then test which answer choice produces that same number. This turns an abstract algebra question into ordinary arithmetic you can check quickly.',
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A question asks which expression is equivalent to (x + 2)² - 4 for all values of x. Pick a simple number, like x = 3: (3 + 2)² - 4 = 25 - 4 = 21. Now plug x = 3 into each answer choice — the one that also equals 21 is the equivalent expression.',
          },
        ],
      },
      {
        id: 'strategy-pacing',
        heading: 'Time Management and the Adaptive Format',
        paragraphs: [
          'Both sections are split into two modules, and how well you do on the first module determines whether the second module draws from a harder or easier question pool — so it\'s worth staying accurate and unhurried on the first module rather than rushing through it. Within any module, don\'t let one hard question eat up several minutes: use the on-screen "mark for review" tool to flag it, move on, and come back with whatever time is left at the end using the question navigator.',
        ],
      },
      {
        id: 'strategy-calculator',
        heading: 'Using the Desmos Calculator Wisely',
        paragraphs: [
          "The built-in graphing calculator is available on every math question, not just some of them. It's especially useful for:",
          {
            type: 'list',
            items: [
              'Graphing both sides of an equation and reading off the intersection point as the solution to a system',
              'Graphing a function to check its vertex or intercepts',
              "Quickly running arithmetic you'd rather not do by hand",
            ],
          },
          "It's a tool for checking and speeding up work you understand — some questions are still faster to reason through directly than to type in, so don't reach for it automatically on every question.",
        ],
      },
    ],
  },
  {
    id: 'part-study-plan',
    title: 'How to Study for the Whole Test',
    sections: [
      {
        id: 'study-diagnose',
        heading: 'Diagnose Your Weak Domains First',
        paragraphs: [
          'Use the Practice Quizzes, organized by the same eight domains used throughout this site (four in Reading and Writing, four in Math), to find out where you\'re actually losing points before you start studying. Spend most of your study time on your lowest-scoring domains rather than re-studying material you\'ve already mastered — the Progress page tracks your accuracy by domain over time so you can watch this shift.',
        ],
      },
      {
        id: 'study-rhythm',
        heading: 'A Weekly Study Rhythm',
        paragraphs: [
          {
            type: 'steps',
            intro: 'A study session works best in this order:',
            steps: [
              "Read the relevant section of a domain guide for the concept you're weakest in.",
              'Immediately take a practice quiz in that same domain to apply what you just read while it\'s fresh.',
              "Use flashcards for quick daily review of rules and terms you've already studied once.",
              'Review the daily vocabulary word for steady, long-term words-in-context vocabulary growth.',
              'Every week or two, take a full timed practice exam to track your overall progress and build the stamina and pacing you\'ll need on test day.',
            ],
          },
        ],
      },
      {
        id: 'study-review-mistakes',
        heading: 'Review Every Wrong Answer',
        paragraphs: [
          'Reviewing why an answer was wrong, not just noting what the right answer was, is what actually raises your score. For each mistake, decide which of three things happened:',
          {
            type: 'list',
            items: [
              'A careless error — you understood the concept but made a slip.',
              "A concept gap — you didn't know the underlying rule or method.",
              'A misread question — you answered a question other than the one that was actually asked.',
            ],
          },
          'Naming the pattern in your own words is what keeps you from repeating it.',
        ],
      },
      {
        id: 'study-final-week',
        heading: 'The Week Before Test Day',
        paragraphs: [
          "In the final week, shift from learning new material to reviewing mistakes you've already identified — cramming brand-new topics this late rarely sticks. Take one last full timed practice exam a few days before the real test, not the night before, so you go in rested rather than exhausted. Confirm your test-day logistics ahead of time:",
          {
            type: 'list',
            items: ['What ID and admission ticket you need', 'Where and when to arrive', "What you're allowed to bring"],
          },
        ],
      },
    ],
  },
];
